/**
 * GENERATED_ASSET — SVG construction context ported from the batch001 static kit (`static-kit/source/construction.ts`).
 * Every seeded channel is `${seed}|${assetId}|${channel}` through the shared SeededRng, exactly as in the kit,
 * so the art stays byte-identical. Definition ids are `${scope}_${name}`; callers pick a scope per content.
 */
import { SeededRng } from '../../utils/SeededRng';
import type {
  DraftArtifact, PaletteRole, Point2, ProtectedVisualRegion, StaticRecipe, SvgAssetArtifact, Tokens, VisualAnchor, VisualBounds,
} from './contracts';
import { REVISION } from './contracts';
import { box, expand, union } from './geometry';
import { validateRecipe, validateTokens } from './validation';

type Attrs = Record<string, string | number | undefined>;

/** Fixed 3-decimal SVG number; non-finite geometry is a construction error. */
export function n(v: number): string {
  if (!Number.isFinite(v)) throw new Error('nonfinite geometry');
  return String(Math.round(v * 1000) / 1000);
}

export function esc(v: string): string {
  return v.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

export function el(tag: string, attrs: Attrs, body = ''): string {
  const a = Object.entries(attrs)
    .filter(([, v]) => v !== undefined)
    .map(([k, v]) => `${k}="${typeof v === 'number' ? n(v) : esc(v!)}"`)
    .join(' ');
  return body ? `<${tag} ${a}>${body}</${tag}>` : `<${tag} ${a}/>`;
}

export function anchor(name: string, x: number, y: number, role: VisualAnchor['role'] = 'attachment'): VisualAnchor {
  return { name, point: { x, y }, role };
}

export class Construction {
  readonly defs: string[] = [];
  readonly content: VisualBounds[] = [];
  readonly paint: VisualBounds[] = [];
  readonly recipe: StaticRecipe;
  readonly tokens: Tokens;
  private readonly names = new Set<string>();

  constructor(recipe: unknown, tokens: unknown, readonly scope: string) {
    this.recipe = validateRecipe(recipe);
    this.tokens = validateTokens(tokens);
    if (!/^[a-z0-9_-]{1,80}$/i.test(scope)) throw new Error('unsafe definition namespace');
  }

  color(role: PaletteRole): string {
    return this.tokens.palettes[this.recipe.paletteId][role];
  }

  random(channel: string): number {
    return new SeededRng(`${this.recipe.seed}|${this.recipe.assetId}|${channel}`).next();
  }

  define(name: string, draw: (id: string) => string): string {
    if (!/^[a-z0-9-]+$/.test(name) || this.names.has(name)) throw new Error('duplicate/invalid definition');
    this.names.add(name);
    const id = `${this.scope}_${name}`;
    this.defs.push(draw(id));
    return `url(#${id})`;
  }

  /** Structural paths extend content bounds; every path extends paint bounds by half its stroke. */
  path(d: string, points: readonly Point2[], fill: string, stroke?: string, width = 0, extra: Attrs = {}, structural = true): string {
    const b = box(points);
    if (structural) this.content.push(b);
    this.paint.push(expand(b, width / 2));
    return el('path', {
      d, fill, stroke, 'stroke-width': stroke ? width : undefined, 'stroke-linecap': 'round', 'stroke-linejoin': 'round', ...extra,
    });
  }

  ellipse(cx: number, cy: number, rx: number, ry: number, fill: string, extra: Record<string, string | number> = {}, structural = true): string {
    const b = { x: cx - rx, y: cy - ry, width: 2 * rx, height: 2 * ry };
    if (structural) this.content.push(b);
    this.paint.push(b);
    return el('ellipse', { cx, cy, rx, ry, fill, ...extra });
  }

  finish(
    body: string,
    anchors: VisualAnchor[],
    regions: ProtectedVisualRegion[] = [],
    origin: DraftArtifact['origin'] = { kind: 'bottom-center', point: { x: 0, y: 0 } },
    geometry: DraftArtifact['geometry'] = {},
    layers: DraftArtifact['layers'] = [],
    role: SvgAssetArtifact['renderRole'] = 'dressing',
  ): DraftArtifact {
    const contentBounds = union(this.content);
    const paintBounds = union(this.paint);
    const viewBox = expand(paintBounds, this.tokens.style.samplingPadding);
    const defs = this.defs.join('');
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${n(viewBox.width)}" height="${n(viewBox.height)}" `
      + `viewBox="${n(viewBox.x)} ${n(viewBox.y)} ${n(viewBox.width)} ${n(viewBox.height)}">`
      + `<title>${esc(this.recipe.id)}</title><defs>${defs}</defs>${body}</svg>\n`;
    return {
      kind: 'svg-artifact',
      claim: 'CANDIDATE_NOT_INTEGRATED',
      assetId: this.recipe.assetId,
      recipeId: this.recipe.id,
      generatorId: this.recipe.generatorId,
      generatorRevision: REVISION,
      svg,
      defs,
      body,
      origin,
      contentBounds,
      paintBounds,
      viewBox,
      anchors: [anchor('origin', origin.point.x, origin.point.y, 'origin'), ...anchors],
      protectedRegions: regions,
      renderRole: role,
      gameplayGeometry: 'not-generated',
      geometry,
      layers,
      boundsMethod: 'control-hull-and-stroke',
    };
  }
}
